import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Mail, Phone, MapPin, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Layout from '../components/layout/Layout';
import { useMockData } from '../data/useMockData';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';

const ContactPage = () => {
  const { t } = useTranslation();
  const { companyInfo, contactFormFields } = useMockData();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    projectType: '',
    budget: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Initialize EmailJS with public key
  React.useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    if (publicKey) {
      emailjs.init(publicKey);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // EmailJS configuration
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'contact_service';
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_hau5zla';
      
      // Prepare template parameters (matching the test structure)
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        company: formData.company,
        project_type: formData.projectType,
        budget: formData.budget || 'Non spécifié',
        message: formData.message,
        email: formData.email, // Added for compatibility
      };
      
      // Send email via EmailJS (public key already initialized)
      await emailjs.send(serviceId, templateId, templateParams);
      
      setIsSubmitted(true);
      toast.success(t('contact.thankYou'));
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('contact.errorSubmitting') || 'Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Layout>
        <section className="pt-32 pb-24 min-h-screen flex items-center">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 w-full">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-20 h-20 bg-[#00FFD1] mx-auto mb-8 flex items-center justify-center">
                <Check className="w-10 h-10 text-black" />
              </div>
              <h1 className="text-4xl md:text-5xl font-semibold text-white mb-6">
                {t('contact.thankYou')}
              </h1>
              <p className="text-lg text-white/60 mb-10">
                {t('contact.thankYouDesc')}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/case-studies">
                  <Button className="bg-[#00FFD1] text-black hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] px-6 py-3">
                    {t('contact.viewCaseStudies')}
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="bg-transparent text-white border border-white/20 hover:bg-white hover:text-black px-6 py-3">
                    {t('contact.backHome')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="grid-pattern absolute inset-0 opacity-30" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-semibold text-white mb-6">
              {t('contact.pageTitle')}
            </h1>
            <p className="text-xl text-white/70 leading-relaxed">
              {t('contact.pageDescription')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white">{t('contact.form.name')} *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/60 h-14 focus:border-[#00FFD1]/50"
                      placeholder={t('contact.form.namePlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-white">{t('contact.form.company')} *</Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      required
                      value={formData.company}
                      onChange={handleChange}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/60 h-14 focus:border-[#00FFD1]/50"
                      placeholder={t('contact.form.companyPlaceholder')}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white">{t('contact.form.email')} *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/60 h-14 focus:border-[#00FFD1]/50"
                    placeholder={t('contact.form.emailPlaceholder')}
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-white">{t('contact.form.projectType')} *</Label>
                    <Select onValueChange={(value) => handleSelectChange('projectType', value)} required>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white placeholder:text-white/60 h-14 focus:ring-[#00FFD1]/50">
                        <SelectValue placeholder={t('contact.form.projectTypePlaceholder')} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        <SelectItem value="web" className="text-white hover:bg-white/10">{t('contact.form.projectTypes.web')}</SelectItem>
                        <SelectItem value="mobile" className="text-white hover:bg-white/10">{t('contact.form.projectTypes.mobile')}</SelectItem>
                        <SelectItem value="backend" className="text-white hover:bg-white/10">{t('contact.form.projectTypes.backend')}</SelectItem>
                        <SelectItem value="cloud" className="text-white hover:bg-white/10">{t('contact.form.projectTypes.cloud')}</SelectItem>
                        <SelectItem value="consulting" className="text-white hover:bg-white/10">{t('contact.form.projectTypes.consulting')}</SelectItem>
                        <SelectItem value="other" className="text-white hover:bg-white/10">{t('contact.form.projectTypes.other')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">{t('contact.form.budget')}</Label>
                    <Select onValueChange={(value) => handleSelectChange('budget', value)}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white placeholder:text-white/60 h-14 focus:ring-[#00FFD1]/50">
                        <SelectValue placeholder={t('contact.form.budgetPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        <SelectItem value="<50k" className="text-white hover:bg-white/10">{t('contact.form.budgets.small')}</SelectItem>
                        <SelectItem value="50-150k" className="text-white hover:bg-white/10">{t('contact.form.budgets.medium')}</SelectItem>
                        <SelectItem value="150-500k" className="text-white hover:bg-white/10">{t('contact.form.budgets.large')}</SelectItem>
                        <SelectItem value=">500k" className="text-white hover:bg-white/10">{t('contact.form.budgets.enterprise')}</SelectItem>
                        <SelectItem value="not-sure" className="text-white hover:bg-white/10">{t('contact.form.budgets.notSure')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-white">{t('contact.form.message')} *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/60 focus:border-[#00FFD1]/50 resize-none"
                    placeholder={t('contact.form.messagePlaceholder')}
                  />
                </div>
                
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="bg-[#00FFD1] text-black hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] px-8 py-4 text-lg font-medium h-auto w-full md:w-auto disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                      {t('contact.form.sending')}
                    </>
                  ) : (
                    <>
                      {t('contact.form.submit')}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>
            
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 space-y-8">
                <div className="p-8 bg-white/[0.02] border border-white/10">
                  <h2 className="text-xl font-semibold text-white mb-6">{t('contact.whatNext')}</h2>
                  <ol className="space-y-4">
                    <li className="flex gap-4">
                      <span className="w-6 h-6 bg-[#00FFD1] text-black flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                        1
                      </span>
                      <p className="text-white/60 text-sm">
                        {t('contact.step1')}
                      </p>
                    </li>
                    <li className="flex gap-4">
                      <span className="w-6 h-6 bg-[#00FFD1] text-black flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                        2
                      </span>
                      <p className="text-white/60 text-sm">
                        {t('contact.step2')}
                      </p>
                    </li>
                    <li className="flex gap-4">
                      <span className="w-6 h-6 bg-[#00FFD1] text-black flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                        3
                      </span>
                      <p className="text-white/60 text-sm">
                        {t('contact.step3')}
                      </p>
                    </li>
                  </ol>
                </div>
                
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-white">{t('contact.otherWays')}</h2>
                  <a 
                    href={`mailto:${companyInfo.email}`}
                    className="flex items-center gap-4 text-white/60 hover:text-[#00FFD1] transition-colors duration-300"
                  >
                    <div className="w-12 h-12 bg-white/5 flex items-center justify-center">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-sm text-white/70">{t('contact.emailLabel')}</div>
                      <div className="text-white">{companyInfo.email}</div>
                    </div>
                  </a>
                  <a 
                    href={`tel:${companyInfo.phone}`}
                    className="flex items-center gap-4 text-white/60 hover:text-[#00FFD1] transition-colors duration-300"
                  >
                    <div className="w-12 h-12 bg-white/5 flex items-center justify-center">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-sm text-white/70">{t('contact.phoneLabel')}</div>
                      <div className="text-white">{companyInfo.phone}</div>
                    </div>
                  </a>
                  <div className="flex items-center gap-4 text-white/60">
                    <div className="w-12 h-12 bg-white/5 flex items-center justify-center">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-sm text-white/70">{t('contact.officeLabel')}</div>
                      <div className="text-white text-sm">{companyInfo.address}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
